import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { SegmentButton } from '@/components/ui/segment-button';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { DailySpendChart } from './DailySpendChart';
import {
  channelLabel,
  deliveryDays,
  formatCount,
  formatDateTime,
  formatDay,
  formatDuration,
  formatPercent,
  formatUsd,
  jobLabel,
  judgeLabel,
  personLabel,
  RESULT_LABELS,
  runsNote,
  SOURCE_LABELS,
} from './slack-bot-view';
import {
  useSlackDashboardQuery,
  useSlackDeliveryQuery,
  useSlackInstallsQuery,
} from './useSlackDashboard';

import type {
  DashboardPeriod,
  DashboardRest,
  SlackDashboard,
  SlackDeliverySlo,
} from './slack-bot.types';
import type { ReactNode } from 'react';

const PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: 'month', label: 'This month' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

function Failure({ error }: { error: Error }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive px-4 py-3 text-sm"
    >
      {error.message}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function Stat({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4 pt-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {detail !== undefined && (
          <p className="text-xs text-muted-foreground">{detail}</p>
        )}
      </CardContent>
    </Card>
  );
}

function MonthCard({ month }: { month: SlackDashboard['month'] }) {
  const cap = month.cap_micro_usd;
  const spentShare = cap === 0 ? 0 : (month.spent_micro_usd / cap) * 100;
  const projected = month.projected_micro_usd;
  const projectedOver = projected !== null && projected > cap;
  const monthName = new Date(`${month.month}T00:00:00Z`).toLocaleString(
    'en-US',
    { month: 'long', timeZone: 'UTC' }
  );
  const parts = [
    { label: 'Turns', value: month.turns_micro_usd },
    { label: 'Untagged judge', value: month.judge_micro_usd },
    { label: 'Channel summaries', value: month.summaries_micro_usd },
    { label: 'Other', value: month.other_micro_usd },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Spent in {monthName}</CardTitle>
        <CardDescription>Against the cap, whatever the range</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-4xl font-semibold tabular-nums">
            {formatUsd(month.spent_micro_usd)}
          </span>
          <span className="text-sm text-muted-foreground">
            of {formatUsd(cap)} ({formatPercent(month.spent_micro_usd, cap)})
          </span>
        </p>
        <div
          role="progressbar"
          aria-label="Spend against the monthly cap"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(100, Math.round(spentShare))}
          className="relative h-2 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary"
            style={{ width: `${Math.min(100, spentShare)}%` }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <span>
            {projected === null
              ? 'Too early in the month to project'
              : `On pace for ${formatUsd(projected)} by month end`}
          </span>
          {projectedOver && (
            <span className="inline-flex items-center gap-1 font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Over the cap at this pace
            </span>
          )}
          {month.capped_at !== null && (
            <span className="inline-flex items-center gap-1 font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Capped {formatDateTime(month.capped_at)}
            </span>
          )}
          {month.reserved_micro_usd > 0 && (
            <span className="text-muted-foreground">
              {formatUsd(month.reserved_micro_usd)} held for running turns
            </span>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          {parts.map((part) => (
            <div key={part.label}>
              <dt className="text-muted-foreground">{part.label}</dt>
              <dd className="tabular-nums">{formatUsd(part.value)}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-muted-foreground">
          Limits: {formatUsd(month.turn_cap_micro_usd)} a turn, and{' '}
          {month.asker_share_pct}% of the cap (
          {formatUsd((cap * month.asker_share_pct) / 100)}) for one person.
          {month.alerted_80_at !== null
            ? ` The 80% alert went out ${formatDateTime(month.alerted_80_at)}.`
            : month.alerted_50_at !== null
              ? ` The 50% alert went out ${formatDateTime(month.alerted_50_at)}.`
              : ''}
        </p>
      </CardContent>
    </Card>
  );
}

function HealthStats({ totals }: { totals: SlackDashboard['totals'] }) {
  const ended =
    totals.answered +
    totals.no_answer +
    totals.kept_quiet +
    totals.failed +
    totals.stopped +
    totals.refused;
  const votes = totals.thumbs_up + totals.thumbs_down;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      <Stat
        label="Spent in this range"
        value={formatUsd(totals.cost_micro_usd)}
        detail={
          totals.p50_turn_micro_usd === null
            ? 'No costed turns'
            : `Typical turn ${formatUsd(totals.p50_turn_micro_usd)}`
        }
      />
      <Stat
        label="Turns"
        value={formatCount(totals.turns)}
        detail={
          [
            totals.running > 0
              ? `${formatCount(totals.running)} still running`
              : null,
            totals.continued > 0
              ? `${formatCount(totals.continued)} parts of long jobs that went on`
              : null,
          ]
            .filter((part) => part !== null)
            .join(', ') || undefined
        }
      />
      <Stat
        label="Answered"
        value={formatPercent(totals.answered, ended)}
        detail={`${formatCount(totals.answered)} answered, ${formatCount(totals.no_answer)} gave no answer, ${formatCount(totals.kept_quiet)} kept quiet, ${formatCount(totals.refused)} refused, ${formatCount(totals.stopped)} stopped`}
      />
      <Stat
        label="Failed"
        value={formatCount(totals.failed)}
        detail={`${formatPercent(totals.failed, ended)} of finished turns`}
      />
      <Stat
        label="Response time"
        value={formatDuration(totals.p50_ms)}
        detail={`Typical. Slow turns (p95): ${formatDuration(totals.p95_ms)}. Votes: ${
          votes === 0
            ? 'none yet'
            : `${formatCount(totals.thumbs_up)} up, ${formatCount(totals.thumbs_down)} down`
        }.`}
      />
    </div>
  );
}

// A ranked list with a thin bar beside each cost
function BreakdownCard({
  title,
  description,
  nameHeading,
  rows,
  rest,
  total,
}: {
  title: string;
  description?: string;
  nameHeading: string;
  rows: { key: string; name: string; cost: number; turns: number }[];
  rest?: DashboardRest;
  total: number;
}) {
  const largest = Math.max(...rows.map((row) => row.cost), 1);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description !== undefined && (
          <CardDescription>{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty>Nothing in this range.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{nameHeading}</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Share</TableHead>
                <TableHead className="text-right">Turns</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="max-w-56">
                    <div className="truncate" title={row.name}>
                      {row.name}
                    </div>
                    <div
                      className="mt-1 h-1 rounded-full bg-primary/70"
                      style={{ width: `${(row.cost / largest) * 100}%` }}
                      aria-hidden
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUsd(row.cost)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(row.cost, total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(row.turns)}
                  </TableCell>
                </TableRow>
              ))}
              {rest !== undefined && rest.count > 0 && (
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {formatCount(rest.count)} more
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUsd(rest.cost_micro_usd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(rest.cost_micro_usd, total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(rest.turns)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Breakdowns({ dashboard }: { dashboard: SlackDashboard }) {
  const total = dashboard.totals.cost_micro_usd;
  const turnTotal = dashboard.totals.turn_cost_micro_usd;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <BreakdownCard
        title="By person"
        description="Who asked. Untagged turns and background work are nobody's, so this can add up to less."
        nameHeading="Person"
        total={total}
        rows={dashboard.by_person.rows.map((row) => ({
          key: row.slack_user_id,
          name: personLabel({
            name: row.name,
            slackUserId: row.slack_user_id,
          }),
          cost: row.cost_micro_usd,
          turns: row.turns,
        }))}
        rest={dashboard.by_person.rest}
      />
      <BreakdownCard
        title="By channel"
        description="Every DM is one row, and every group DM another."
        nameHeading="Channel"
        total={total}
        rows={dashboard.by_channel.rows.map((row) => ({
          key: `${row.kind ?? ''}:${row.channel_id ?? ''}`,
          name: channelLabel({
            kind: row.kind,
            name: row.name,
            channelId: row.channel_id,
          }),
          cost: row.cost_micro_usd,
          turns: row.turns,
        }))}
        rest={dashboard.by_channel.rest}
      />
      <BreakdownCard
        title="By trigger"
        description="What set it off. The judge and summaries run with no turn."
        nameHeading="Trigger"
        total={total}
        rows={dashboard.by_source.map((row) => ({
          key: row.source,
          name: SOURCE_LABELS[row.source],
          cost: row.cost_micro_usd,
          turns: row.turns,
        }))}
      />
      <BreakdownCard
        title="By model"
        description="Turns only, by the model alias they ran on."
        nameHeading="Model"
        total={turnTotal}
        rows={dashboard.by_model.map((row) => ({
          key: row.model ?? 'none',
          name: row.model ?? 'None (never reached a model)',
          cost: row.cost_micro_usd,
          turns: row.turns,
        }))}
      />
    </div>
  );
}

function CostliestTurns({
  turns,
}: {
  turns: SlackDashboard['costliest_turns'];
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Most expensive turns</CardTitle>
        <CardDescription>
          Slack decides who can open a thread, so a private one only opens for
          its members.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {turns.length === 0 ? (
          <Empty>No turns in this range.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="text-right">Took</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Thread</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {turns.map((turn) => (
                <TableRow key={turn.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(turn.created_at)}
                  </TableCell>
                  <TableCell>
                    {personLabel({
                      name: turn.person_name,
                      slackUserId: turn.slack_user_id,
                    })}
                  </TableCell>
                  <TableCell>
                    {channelLabel({
                      kind: turn.channel_kind,
                      name: turn.channel_name,
                      channelId: turn.channel_id,
                    })}
                  </TableCell>
                  <TableCell>{SOURCE_LABELS[turn.source]}</TableCell>
                  <TableCell>{turn.model ?? '–'}</TableCell>
                  <TableCell>
                    {turn.outcome === 'failed' ? (
                      <Badge variant="destructive">Failed</Badge>
                    ) : (
                      RESULT_LABELS[turn.outcome]
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {formatDuration(turn.total_ms)}
                  </TableCell>
                  <TableCell
                    className="text-right tabular-nums"
                    title={`${formatCount(turn.input_tokens)} in, ${formatCount(turn.output_tokens)} out, ${formatCount(turn.cache_read_tokens)} cache read, ${formatCount(turn.cache_write_tokens)} cache write`}
                  >
                    {formatUsd(turn.cost_micro_usd)}
                  </TableCell>
                  <TableCell>
                    {turn.thread_url === null ? (
                      '–'
                    ) : (
                      <a
                        href={turn.thread_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline underline-offset-2"
                      >
                        Open
                        <ExternalLink className="h-3 w-3" aria-hidden />
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Jobs({ jobs }: { jobs: SlackDashboard['jobs'] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Scheduled jobs</CardTitle>
        <CardDescription>
          Live now, or with a run in this range. A title shows only for a job
          that lives and posts in public channels.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {jobs.length === 0 ? (
          <Empty>No scheduled jobs in this range.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Set up by</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Per run</TableHead>
                <TableHead className="text-right">Estimate</TableHead>
                <TableHead>Next run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="max-w-72">
                    <div className="truncate">{jobLabel(job)}</div>
                    <div className="text-xs text-muted-foreground">
                      {channelLabel({
                        kind: job.channel_kind,
                        name: job.channel_name,
                        channelId: job.channel_id,
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {job.schedule ?? '–'}
                  </TableCell>
                  <TableCell>
                    {job.consecutive_failures > 0 ? (
                      <Badge variant="destructive">
                        Failing ({job.consecutive_failures} in a row)
                      </Badge>
                    ) : (
                      <span className="capitalize">{job.state}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {personLabel({
                      name: job.person_name,
                      slackUserId: job.slack_user_id,
                    })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(job.runs)}
                    {runsNote(job)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUsd(job.cost_micro_usd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {job.per_run_micro_usd === null
                      ? '–'
                      : formatUsd(job.per_run_micro_usd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {job.estimated_run_micro_usd === null
                      ? '–'
                      : formatUsd(job.estimated_run_micro_usd)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(job.next_run_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function JudgeCard({ judge }: { judge: SlackDashboard['judge'] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Untagged judge</CardTitle>
        <CardDescription>
          {judge.judged === 0
            ? 'What it decided about messages nobody tagged the bot in.'
            : `${formatCount(judge.judged)} messages judged, ${formatCount(judge.shadow)} in shadow. The judge cost ${formatUsd(judge.judge_micro_usd)}, and the turns it opened ${formatUsd(judge.turns_micro_usd)}.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {judge.judged === 0 ? (
          <Empty>Nothing judged in this range.</Empty>
        ) : (
          <Table>
            <TableBody>
              {judge.outcomes.map((row) => (
                <TableRow key={row.outcome}>
                  <TableCell>{judgeLabel(row.outcome)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(row.count)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(row.count, judge.judged)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function TeamNotesCard({ notes }: { notes: SlackDashboard['team_notes'] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">New team notes</CardTitle>
        <CardDescription>
          {notes.total === 0
            ? 'What the bot saved for the whole team. Worth a look for a bad one.'
            : `${formatCount(notes.total)} new or changed${notes.notes.length < notes.total ? `, the newest ${notes.notes.length} here` : ''}. Worth a look for a bad one.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {notes.notes.length === 0 ? (
          <Empty>No team notes new or changed in this range.</Empty>
        ) : (
          <Table>
            <TableBody>
              {notes.notes.map((note) => (
                <TableRow key={note.id}>
                  <TableCell>
                    <div>{note.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {note.is_new ? 'New' : `Changed, version ${note.version}`}
                      {' by '}
                      {personLabel({
                        name: note.person_name,
                        slackUserId: note.slack_user_id,
                      })}
                      , {formatDateTime(note.updated_at)}, saved from{' '}
                      {note.source_label === 'dm'
                        ? 'a DM'
                        : note.source_label === 'private'
                          ? 'a private channel'
                          : note.source_label === 'public'
                            ? 'a public channel'
                            : 'where it was mentioned'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant={
                        note.status === 'verified' ? 'secondary' : 'outline'
                      }
                    >
                      {note.status === 'verified' ? 'Verified' : 'Unverified'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryCard({
  isLoading,
  error,
  slo,
}: {
  isLoading: boolean;
  error: Error | null;
  slo: SlackDeliverySlo | undefined;
}) {
  const days = slo === undefined ? [] : deliveryDays(slo.days);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Delivery health</CardTitle>
        <CardDescription>
          {slo === undefined
            ? 'Always the last 7 days.'
            : `Always the last 7 days. Targets: ${Math.round(slo.targets.first_status_share * 100)}% of first statuses within ${formatDuration(slo.targets.first_status_ms)}, every message answered within ${formatDuration(slo.targets.answer_ms)}, no mention caught only by the sweep.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error !== null ? (
          <Failure error={error} />
        ) : isLoading ? (
          <Empty>Loading...</Empty>
        ) : days.length === 0 ? (
          <Empty>No messages to the bot in the last 7 days.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead className="text-right">Turns</TableHead>
                <TableHead className="text-right">
                  First status on time
                </TableHead>
                <TableHead className="text-right">Slowest p95 answer</TableHead>
                <TableHead className="text-right">Late</TableHead>
                <TableHead className="text-right">Silent</TableHead>
                <TableHead className="text-right">Swept</TableHead>
                <TableHead>Targets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {days.map((day) => (
                <TableRow key={day.day}>
                  <TableCell>{formatDay(day.day)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(day.turns)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(day.statusWithin, day.statusEligible)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDuration(day.worstAnswerP95Ms)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(day.answeredLate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(day.silent)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(day.swept)}
                  </TableCell>
                  <TableCell>
                    {day.breaches.length === 0 ? (
                      'Met'
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {day.breaches.map((breach) => (
                          <Badge key={breach} variant="destructive">
                            {breach}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Dashboard({
  installId,
  period,
}: {
  installId: string;
  period: DashboardPeriod;
}) {
  const dashboard = useSlackDashboardQuery(installId, period);
  const delivery = useSlackDeliveryQuery(installId);
  if (dashboard.error !== null) return <Failure error={dashboard.error} />;
  if (dashboard.data === undefined) return <Empty>Loading...</Empty>;
  const data = dashboard.data;
  return (
    <div className="space-y-4" aria-busy={dashboard.isPlaceholderData}>
      {dashboard.isPlaceholderData && (
        <p role="status" className="text-sm text-muted-foreground">
          Updating to this range...
        </p>
      )}
      <MonthCard month={data.month} />
      <HealthStats totals={data.totals} />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Spend by day</CardTitle>
          <CardDescription>
            {formatDay(data.window.from)} to {formatDay(data.window.until)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DailySpendChart days={data.days} />
        </CardContent>
      </Card>
      <Breakdowns dashboard={data} />
      <CostliestTurns turns={data.costliest_turns} />
      <Jobs jobs={data.jobs} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <JudgeCard judge={data.judge} />
        <TeamNotesCard notes={data.team_notes} />
      </div>
      <DeliveryCard
        isLoading={delivery.isLoading}
        error={delivery.error}
        slo={delivery.data}
      />
    </div>
  );
}

// What the Slack bot spends, on what, and whether it works: one read-only page
// over the install the viewer picks
export function SlackBotPage() {
  const installs = useSlackInstallsQuery();
  const [picked, setPicked] = useState<string | null>(null);
  const [period, setPeriod] = useState<DashboardPeriod>('month');
  const listed = installs.data ?? [];
  const installId = picked ?? listed[0]?.id ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Slack bot</h1>
          <p className="text-sm text-muted-foreground">
            What we spend, on what, and whether it works. Days and times are
            UTC.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {listed.length > 1 && (
            <Select
              aria-label="Workspace"
              className="w-56"
              value={installId ?? ''}
              onChange={(event) => setPicked(event.target.value)}
            >
              {listed.map((install) => (
                <option key={install.id} value={install.id}>
                  {install.team_name}
                  {install.status === 'active' ? '' : ` (${install.status})`}
                </option>
              ))}
            </Select>
          )}
          <SegmentButton
            options={PERIODS}
            selected={period}
            onSelect={setPeriod}
          />
        </div>
      </div>
      {installs.error !== null ? (
        <Failure error={installs.error} />
      ) : installs.isLoading ? (
        <Empty>Loading...</Empty>
      ) : installId === null ? (
        <Empty>No Slack workspace has installed the bot yet.</Empty>
      ) : (
        <Dashboard installId={installId} period={period} />
      )}
    </div>
  );
}
